import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import './explore.css';

import locationIcon from '../assets/icons/locationIcon.svg';
import filter from '../assets/icons/filter.svg';
import BottomNav from '../components/layout/bottomNav';
import MobileHeader from '../components/layout/mobileHeader';

const Explore = () => {
  const navigate = useNavigate();

  const [exhibitors, setExhibitors] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch] = useState('');
  const [audience, setAudience] = useState('All');
  const [hall, setHall] = useState('All');
  const [language, setLanguage] = useState('All');
  const [maxPrice, setMaxPrice] = useState(0);

  useEffect(() => {
    const loadExhibitors = async () => {
      const { data, error } = await supabase
        .from('exhibitors')
        .select('*')
        .eq('status', 'Approved')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading exhibitors:', error.message);
        return;
      }

      setExhibitors(data || []);
    };

    loadExhibitors();

    const channel = supabase
      .channel('mobile_explore_exhibitors')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'exhibitors',
        },
        () => loadExhibitors()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filteredExhibitors = useMemo(() => {
    return exhibitors.filter((exhibitor) => {
      const matchesSearch =
        exhibitor.name?.toLowerCase().includes(search.toLowerCase()) ||
        exhibitor.description?.toLowerCase().includes(search.toLowerCase()) ||
        exhibitor.category?.toLowerCase().includes(search.toLowerCase());

      const matchesAudience =
        audience === 'All' || exhibitor.audience === audience;

      const matchesHall =
        hall === 'All' || exhibitor.hall === hall;

      const matchesLanguage =
        language === 'All' || exhibitor.language === language;

      return matchesSearch && matchesAudience && matchesHall && matchesLanguage;
    });
  }, [exhibitors, search, audience, hall, language]);

  return (
    <main className="explorePage">
        <MobileHeader />

      <div className="exploreSearchRow">
        <div className="exploreSearchBox">
          <span>⌕</span>
          <input
            type="text"
            placeholder="Search exhibitors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button
          className="filterCircleBtn"
          onClick={() => setShowFilters(!showFilters)}
        >
          <img src={filter} alt="filter icon" />
        </button>
      </div>

      {showFilters ? (
        <section className="filtersPage">
          <h1>Filters</h1>

          <FilterGroup
            title="Target Audience"
            options={['All', 'Kids', 'Young Adults', 'Adults']}
            active={audience}
            onChange={setAudience}
          />

          <FilterGroup
            title="Location"
            options={['All', 'Hall 1', 'Hall 2', 'Hall 3', 'Hall 4']}
            active={hall}
            onChange={setHall}
          />

          <div className="filterBlock">
            <h2>Price Range</h2>

            <div className="priceValue">{maxPrice} L.E</div>

            <input
              className="priceSlider"
              type="range"
              min="0"
              max="500"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
            />
          </div>

          <FilterGroup
            title="Language"
            options={['All', 'English', 'Arabic', 'French', 'Other']}
            active={language}
            onChange={setLanguage}
          />

          <button
            className="applyBtn"
            onClick={() => setShowFilters(false)}
          >
            Apply
          </button>
        </section>
      ) : (
        <section className="exhibitorsList">
          {filteredExhibitors.map((exhibitor) => (
            <div className="mobileExhibitorCard" key={exhibitor.id}>
              <div className="mobileLogoFrame">
                {exhibitor.logo_url ? (
                  <img src={exhibitor.logo_url} alt={exhibitor.name} />
                ) : (
                  <h3>{exhibitor.name}</h3>
                )}
              </div>

              <div className="mobileExhibitorInfo">
                <h2>{exhibitor.name}</h2>
                <p>{exhibitor.description}</p>

                <div className="categoryTags">
                  {exhibitor.language && <span>{exhibitor.language}</span>}
                  {exhibitor.category && <span>{exhibitor.category}</span>}
                  {exhibitor.audience && <span>{exhibitor.audience}</span>}
                </div>

                <div className="locationRow">
                  <img src={locationIcon} alt="Location" />
                  <p>{exhibitor.hall} - Booth {exhibitor.booth}</p>
                </div>

                <div className="cardActions">
                  <button className="viewBoothBtn">View Booth</button>
                  <button className="mapBtn">🗺</button>
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      <BottomNav />
    </main>
  );
};

const FilterGroup = ({ title, options, active, onChange }) => {
  return (
    <div className="filterBlock">
      <h2>{title}</h2>

      <div className="filterOptions">
        {options.map((option) => (
          <button
            key={option}
            className={active === option ? 'selectedFilter' : ''}
            onClick={() => onChange(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};


export default Explore;